import React, { useState, useContext, useEffect } from "react";
import { Offcanvas } from "react-bootstrap";
import { UserContext } from "../context/UserContext.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import Nav from "./Nav.jsx";
import useIsMobile from "../hooks/useIsMobile";

import { Link } from "react-router-dom";
import { useModal } from "../context/ModalContext";
import "../assets/styles/header.css";
import btnMenu from "../assets/images/svg/btnMenuHome.svg";
import logoPurissima from "../assets/images/svg/logo-purissima-horizontal-brown.svg";
import iconeHeader from "../assets/images/svg/iconeHeader.svg";
import Button from "./Button.jsx";
import Image from "./Image.jsx";
import { SALES_SUSPENDED } from "../config/salesSuspended";
import useExistingAnamnese from "../hooks/useExistingAnamnese";
import { getAuthToken } from "../utils/authToken";

// Âncoras da home (ids em pages/home2/HowWorks, ComoFunciona e Whole)
const HEADER_LINKS = [
  { label: "A Puríssima", hash: "how-works" },
  { label: "Como funciona", hash: "passo-a-passo" },
  { label: "Nossos planos", hash: "whole" },
];

const Header = () => {
  const { openModal } = useModal();
  const [show, setShow] = useState(false);
  const [pendingModal, setPendingModal] = useState(null); // modal a abrir após fechar o offcanvas
  const location = useLocation();
  const navigate = useNavigate();
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const openModalAfterClose = (key, data) => {
    setPendingModal({ key, data });
    setShow(false);
  };

  const handleLoginClick = (e, afterCloseOffcanvas) => {
    e.preventDefault();
    if (SALES_SUSPENDED) {
      if (afterCloseOffcanvas) {
        openModalAfterClose("modalSalesSuspended");
      } else {
        openModal("modalSalesSuspended");
      }
      return;
    }
    if (afterCloseOffcanvas) {
      openModalAfterClose("modalLogin", loginModalData);
    } else {
      openModal("modalLogin", loginModalData);
    }
  };

  // Fecha o menu sempre que a rota mudar (ex: clique em login/perfil, anchors que levam para home, etc.)
  useEffect(() => {
    if (show) setShow(false);
  }, [location.pathname]);

  const { isAuthenticated, loadingAuth, userName, userAnamnesis, currentUser, logout } = useContext(
    UserContext
  );
  const { hasAnamnese, checking } = useExistingAnamnese();
  // "Começar agora" é para visitante ou conta sem anamnese; quem já tem anamnese
  // segue pela conta (Olá, nome), onde estão "Fazer pedido" e "Nova anamnese".
  // Com sessão salva e ainda sem resposta, o botão espera para não piscar e sumir.
  const awaitingSession = loadingAuth && Boolean(getAuthToken());
  const showStartCta = !awaitingSession && (!isAuthenticated || (!checking && !hasAnamnese));
  const gender = userAnamnesis?.gender;
  const isMinhaConta = location.pathname.startsWith("/minha-conta");
  // Preserva a origem do fluxo Básico quando a anamnese é iniciada pela header
  // enquanto o usuário ainda está na própria página /basico.
  const isBasicoPage = location.pathname === "/basico";
  // Ao logar pela header nessa página, retoma o modalPesquisa (origin: basico)
  // em vez de cair no redirecionamento padrão pós-login.
  const loginModalData = isBasicoPage
    ? { resumeType: "modalPesquisa", resumeData: { origin: "basico" } }
    : undefined;
  const isMobile = useIsMobile();
  
  return (
    <>
      <header style={{ top: "var(--staging-banner-height, 0px)" }}>
        <div className="container top-menu" style={{ maxWidth: "1120px" }}>
          <div className="row align-items-center justify-content-between">
            {/* Logo */}
            <div className="col col-lg-auto logo">
              <Link to="/" onClick={handleClose}>
                <img src={logoPurissima} alt="Logo Purissima" />
              </Link>
            </div>

            {/* Links de seção da home (desktop) */}
            <nav className="col d-none d-lg-flex justify-content-center header-links" aria-label="Seções">
              {HEADER_LINKS.map((link) => (
                <Link key={link.hash} to={`/#${link.hash}`} onClick={handleClose}>
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="col col-lg-auto navigation">
              <div className="row justify-content-end align-items-center">
                {/* O login de visitante fica no menu lateral; aqui só a saudação de quem já entrou */}
                {isAuthenticated && (
                  <div
                    className={`col-auto px-lg-2 order-1 order-lg-3 ${
                      showStartCta ? "d-none d-lg-flex" : "d-flex"
                    }`}
                  >
                    <Link
                      to="/minha-conta"
                      className="d-inline-flex align-items-center header-greeting"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="24" viewBox="0 0 22 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="me-2">
                        <circle cx="11" cy="7" r="4.5" />
                        <path d="M2.5 22v-2.5C2.5 16.5 5 14.5 8 14.5h6c3 0 5.5 2 5.5 5V22" />
                      </svg>
                      <span>Olá, {userName}</span>
                    </Link>
                  </div>
                )}

                <div className="col-auto px-2 px-lg-2 order-2 order-lg-5" onClick={handleShow}>
                  <img src={btnMenu} alt="btnMenu" className="btnMenu d-flex" />
                </div>

                {showStartCta && (
                  <div className="col-auto px-0 px-lg-2 order-1 order-lg-4 d-flex">
                    <Button
                      label="Começar agora"
                      classes="btn-purissima btn-menu"
                      onClick={() => openModal("modalPesquisa", isBasicoPage ? { origin: "basico" } : undefined)}
                      delay={300}
                    />
                  </div>
                )}

                <Offcanvas show={show}
                  onHide={handleClose}
                  onExited={() => {
                    if (pendingModal) {
                      openModal(pendingModal.key, pendingModal.data);
                      setPendingModal(null);
                    }
                  }}
                  placement="end">
                  <div className="offcanvas-body p-0 d-flex flex-column h-100 overflow-hidden">
                    <div className="p-3 p-lg-4 pb-0 pb-lg-0">
                      <Offcanvas.Header closeButton>
                        <div className="container">
                          <div className="row align-items-start box-header">
                            <div className="col-auto ps-0">
                              <Image
                                image="avatar-header.png"
                                title="Avatar do usuário"
                                className="avatar"
                              />
                            </div>
                            <div className="col ps-0">
                              <p className="m-0">
                                {currentUser
                                  ? gender === "2"
                                    ? "Bem-vinda"
                                    : "Bem-vindo"
                                  : "Olá,"}
                              </p>

                              <h4 className="m-0 opensans">
                                {userName || "Visitante"}
                              </h4>

                              {isAuthenticated ? (
                                <>
                                  <Link
                                    to="/minha-conta"
                                    className="align-items-center mt-1"
                                    onClick={handleClose}
                                  >
                                    <img
                                      src={iconeHeader}
                                      alt=""
                                      className="me-2"
                                    />
                                    <span className="profile-access m-0">
                                      Acessar meu perfil
                                    </span>
                                  </Link>
                                  <br/>
                                  <Link
                                    to="#"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      await logout();
                                      handleClose();
                                      navigate('/');
                                    }}
                                    className="align-items-center mt-3 text-decoration-none"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="15"
                                      height="14"
                                      viewBox="0 0 19 18"
                                      fill="none"
                                      className="me-2"
                                      stroke="#A7A39A"
                                    >
                                      <path
                                        d="M12 5V3C12 2.46957 11.7893 1.96086 11.4142 1.58579C11.0391 1.21071 10.5304 1 10 1H3C2.46957 1 1.96086 1.21071 1.58579 1.58579C1.21071 1.96086 1 2.46957 1 3V15C1 15.5304 1.21071 16.0391 1.58579 16.4142C1.96086 16.7893 2.46957 17 3 17H10C10.5304 17 11.0391 16.7893 11.4142 16.4142C11.7893 16.0391 12 15.5304 12 15V13M18 9H5M5 9L8 6M5 9L8 12"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span className="profile-access m-0" style={{ fontSize: '13px', color: '#A7A39A' }}>Sair da conta</span>
                                  </Link>
                                </>
                              ) : (
                                <Link
                                  to="#"
                                  className="d-inline-flex align-items-center mt-1"
                                  onClick={(e) => handleLoginClick(e, true)}
                                >
                                  <img
                                    src={iconeHeader}
                                    alt=""
                                    className="me-2"
                                  />
                                  <span className="m-0">Fazer login</span>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </Offcanvas.Header>
                      <div className="mt-3 mt-lg-4 line" />
                    </div>
                    <div className="menu-scroll flex-grow-1 overflow-auto">
                      <Nav onLinkClick={handleClose} />
                    </div>
                    <div className="position-sticky bottom-0 z-1 p-3 p-lg-4 pt-0 pt-lg-0">
                      <div className="line mb-3 mb-lg-4" />
                      {!(isMobile && isMinhaConta) && (
                        <>
                          <div className="menu-fale mb-3 mb-lg-4">
                            <h5><strong>FALE CONOSCO</strong></h5>
                            <div className="row">
                              <div className="col-auto">
                                <p><i className="bi bi-whatsapp me-2" />WhatsApp</p>
                              </div>
                              <div className="col-auto">
                                <p><i className="bi bi-envelope me-2" />contato@purissima.com</p>
                              </div>
                            </div>
                          </div>
                          {hasAnamnese ? (
                            <div className="cta-box">
                              <h4><strong>Sua avaliação já está feita</strong></h4>
                              <p className="mb-3">
                                Faça seu pedido pela sua conta ou cadastre a anamnese de outra pessoa.
                              </p>
                              <Button classes="w-100" url="/minha-conta" label="Ir para minha conta" />
                              <Link
                                to="/minha-conta/minha-familia"
                                className="d-block text-center mt-3"
                                onClick={handleClose}
                              >
                                Nova anamnese
                              </Link>
                            </div>
                          ) : (
                            <div className="cta-box">
                              <h4><strong>Avaliação de Saúde Online</strong></h4>
                              <p className="mb-3">
                                Um questionário clínico que avalia seus sintomas, objetivos, histórico e estilo de vida.
                              </p>
                              <Button
                                classes="w-100"
                                onClick={() => openModalAfterClose("modalPesquisa", isBasicoPage ? { origin: "basico" } : undefined)}
                                delay={300}
                                label="Começar agora"
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Offcanvas>
              </div>
            </div>
          </div>
        </div>
      </header>

    </>

  );
};

export default Header;
